import { centavosAPesos } from '@shared/centavos';
import { esUnidadContinua, precioPorUnidadBase } from '@shared/medidas';

/**
 * Sprint 127 — *"Sale a $560 el kilo"*.
 *
 * El precio de cada tamaño se teclea y **nunca se calcula**: en el catálogo real del negocio el
 * sirloin de 1 kg cuesta $920 y el de 1/2 kg $505, no $460. Una regla de tres le cambiaría sus
 * precios, y eso ya está decidido desde el Sprint 82.
 *
 * Pero al abrir los tamaños chicos (125 g, 100 g, 50 g) aparece un error que antes no cabía: un
 * "1/8 kg" tecleado a $70 sale a **$560 el kilo** cuando el kilo va a $420. Nadie lo ve, porque
 * los dos números son creíbles por separado. Esto los pone lado a lado.
 *
 * No bloquea nada —el negocio puede querer cobrar más caro el tamaño chico, y de hecho lo hace—
 * sólo lo dice en voz alta mientras se captura.
 */
/**
 * Contra qué se compara: el tamaño más grande de la tabla.
 *
 * Es el que el negocio fija primero y del que cuelgan los demás ("el kilo va a $420"). Con un solo
 * tamaño con precio no hay comparación posible y no se inventa ninguna.
 */
export function precioDeReferencia(
  formatos: Array<{ cantidad: number; precio: number }>,
): number | null {
  const candidatos = formatos.filter((f) => f.cantidad > 0 && f.precio > 0);
  if (candidatos.length < 2) return null;
  const mayor = candidatos.reduce((a, b) => (b.cantidad > a.cantidad ? b : a));
  return precioPorUnidadBase(mayor.precio, mayor.cantidad);
}

export function PrecioPorUnidad({
  precioCentavos,
  cantidadBase,
  unidad,
  referenciaCentavos,
}: {
  precioCentavos: number;
  cantidadBase: number;
  unidad: string;
  /** A cuánto sale la unidad base en el tamaño de referencia (el más grande del grupo). */
  referenciaCentavos: number | null;
}) {
  if (!esUnidadContinua(unidad)) return null;

  const porUnidad = precioPorUnidadBase(precioCentavos, cantidadBase);
  if (porUnidad === null) return null;

  /*
    30 % de holgura: por debajo de eso, un tamaño chico más caro es política de precios normal
    (se cobra el corte, no sólo el kilaje). Por encima, casi siempre es un dedazo.
  */
  const desviado =
    referenciaCentavos !== null &&
    referenciaCentavos > 0 &&
    Math.abs(porUnidad - referenciaCentavos) / referenciaCentavos > 0.3;

  return (
    <p className={`mt-1 text-xs ${desviado ? 'font-medium text-amber-700' : 'text-slate-600'}`}>
      Sale a ${centavosAPesos(porUnidad).toFixed(2)} por {unidad}
      {desviado && referenciaCentavos !== null && (
        <> · los demás van a ${centavosAPesos(referenciaCentavos).toFixed(2)}. Revísalo</>
      )}
    </p>
  );
}
